import type { User, UserAddress, UserStatus, UserType } from '@/lib/types/user.types';

/**
 * Transforms backend user data (snake_case) to frontend User type
 * @param backendUser - User data from backend API in snake_case format
 * @returns User object with camelCase properties
 */
export function transformBackendUser(backendUser: any): User {
  const now = new Date().toISOString();

  // Handle user_type: check both snake_case and camelCase, and ensure it's valid
  // This is critical for distinguishing between client and supplier
  let userType: UserType = 'client'; // Default fallback
  if (backendUser.user_type) {
    const ut = String(backendUser.user_type).toLowerCase().trim();
    if (ut === 'supplier' || ut === 'client' || ut === 'admin') {
      userType = ut as UserType;
    }
  } else if (backendUser.userType) {
    const ut = String(backendUser.userType).toLowerCase().trim();
    if (ut === 'supplier' || ut === 'client' || ut === 'admin') {
      userType = ut as UserType;
    }
  }

  // Convert average_rating properly - handle both snake_case (from direct API) and camelCase (from interceptor)
  // The axios interceptor transforms snake_case to camelCase, so we need to check both
  const averageRatingValue =
    backendUser.average_rating !== undefined
      ? backendUser.average_rating
      : backendUser.averageRating !== undefined
        ? backendUser.averageRating
        : null;

  const averageRating =
    averageRatingValue !== null && averageRatingValue !== undefined
      ? Number(averageRatingValue)
      : 0;

  // Convert total_reviews properly - handle both snake_case and camelCase
  const totalReviewsValue =
    backendUser.total_reviews !== undefined
      ? backendUser.total_reviews
      : backendUser.totalReviews !== undefined
        ? backendUser.totalReviews
        : null;

  const totalReviews =
    totalReviewsValue !== null && totalReviewsValue !== undefined ? Number(totalReviewsValue) : 0;

  // Handle verification status - check both snake_case (from backend) and camelCase (from interceptor)
  // Match the pattern used in admin API for consistency
  const isEmailVerified =
    backendUser.is_email_verified !== undefined
      ? Boolean(backendUser.is_email_verified)
      : backendUser.isEmailVerified !== undefined
        ? Boolean(backendUser.isEmailVerified)
        : false;

  const isPhoneVerified =
    backendUser.is_phone_verified !== undefined
      ? Boolean(backendUser.is_phone_verified)
      : backendUser.isPhoneVerified !== undefined
        ? Boolean(backendUser.isPhoneVerified)
        : false;

  // Normalize address for display (support both camelCase and snake_case from backend)
  const rawAddress = backendUser.address || {};
  const addr = typeof rawAddress === 'object' && rawAddress !== null ? rawAddress : ({} as Record<string, unknown>);
  const address: UserAddress = {
    street: String((addr as Record<string, unknown>).street ?? ''),
    city: String((addr as Record<string, unknown>).city ?? ''),
    state: String((addr as Record<string, unknown>).state ?? ''),
    country: String((addr as Record<string, unknown>).country ?? ''),
    postalCode: String((addr as Record<string, unknown>).postalCode ?? (addr as Record<string, unknown>).postal_code ?? ''),
  };

  return {
    id: String(backendUser.id || ''),
    userType: userType,
    email: backendUser.email || '',
    phone: backendUser.phone || '',
    isEmailVerified,
    isPhoneVerified,
    status: (backendUser.status || 'active') as UserStatus,
    fullName: backendUser.full_name || backendUser.fullName || '',
    avatar: backendUser.avatar_url || backendUser.avatar || '',
    dateOfBirth: backendUser.date_of_birth || null,
    nationalId: backendUser.national_id || backendUser.nationalId || null,
    companyName: backendUser.company_name || backendUser.companyName || null,
    commercialRegister:
      backendUser.commercial_register !== undefined && backendUser.commercial_register !== null
        ? backendUser.commercial_register
        : backendUser.commercialRegister ?? null,
    taxNumber:
      backendUser.tax_number !== undefined && backendUser.tax_number !== null
        ? backendUser.tax_number
        : backendUser.taxNumber ?? null,
    averageRating,
    totalReviews,
    completedProjects:
      backendUser.completed_projects !== null && backendUser.completed_projects !== undefined
        ? Number(backendUser.completed_projects)
        : 0,
    walletBalance:
      backendUser.wallet_balance !== null && backendUser.wallet_balance !== undefined
        ? Number(backendUser.wallet_balance)
        : 0,
    // AI Usage Tracking (transformed from snake_case to camelCase)
    aiRequestsCount: backendUser.ai_requests_count || 0,
    aiImprovementsUsed: backendUser.ai_improvements_used || 0,
    aiAverageRating: backendUser.ai_average_rating || 0,
    aiFeatureUsage: {
      requestEnhancement: backendUser.ai_feature_usage?.request_enhancement,
      billOfQuantities: backendUser.ai_feature_usage?.bill_of_quantities,
      paymentSuggestions: backendUser.ai_feature_usage?.payment_suggestions,
      projectPhases: backendUser.ai_feature_usage?.project_phases,
      attachmentAnalysis: backendUser.ai_feature_usage?.attachment_analysis,
    },
    aiPreferenceLevel: backendUser.ai_preference_level || 'medium',
    aiFirstUsedAt: backendUser.ai_first_used_at,
    aiLastUsedAt: backendUser.ai_last_used_at,
    aiFeedbackCount: backendUser.ai_feedback_count || 0,
    address,
    notificationPreferences:
      backendUser.notification_preferences || backendUser.notificationPreferences || {},
    createdAt: backendUser.created_at || now,
    updatedAt: backendUser.updated_at || now,
    lastLoginAt: backendUser.last_login_at || now,
  };
}
